/**
 * PlanReader — the plan document, laid out to be skimmed before it is read.
 *
 * A plan is long by design: the planner writes down what it read, what it
 * will do, how it will prove it, and what a critic said, and none of that is
 * padding. What made the page hard on a person was that every section printed
 * at the same weight in one column, so the eye had no way to find the four
 * facts a reviewer wants first (how, how risky, how many steps, what changes)
 * or to skip the evidence it will come back to only if something looks wrong.
 *
 * So this reader lays out the SAME markdown, display only:
 *
 *   1. A property row at the top: the approach, the risk, and the counts, each
 *      read off its own section, each a jump to that section. Nothing here is
 *      derived; a fact the page did not state does not print. A one-line
 *      Approach or Risk level is said there and not again as a section.
 *   2. Every section folds under its own heading, like a toggle in a document.
 *      The decision sections (Summary, Steps, Acceptance criteria, Files to
 *      change, Commands to run, Test strategy) start open; the evidence
 *      (Grounding, How this plan was made, Critic findings) starts folded under
 *      a one line teaser. The fold animates, and keeps what it hides out of the
 *      document until it is opened.
 *   3. Steps read as a list of titles: a step the planner titled is one line
 *      (number, title, the files it touches) with its detail and cases folded
 *      under it, and each step has an address a person can link to. Files to
 *      change read as a table. Both are parsed back from the markdown by
 *      `planStructure.ts`, and a section it cannot parse prints as written.
 *
 * It splits on the heading level the page writes (`## <title>`, see
 * `workItems/planPage.ts`) through the one shared splitter, so a section the
 * backend adds tomorrow simply renders open with its own heading.
 */
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Markdown } from "../components/Markdown.js";
import { ClampText } from "./ClampText.js";
import { splitSections, type MarkdownSection } from "./planMarkdown.js";
import { PLAN_QUESTIONS_SECTION } from "./planQuestions.js";
import {
  parsePlanFiles,
  parsePlanSteps,
  type PlanFileView,
  type PlanStepView,
} from "./planStructure.js";
import {
  PLAN_BODY_LABEL,
  PLAN_FILE_EDIT,
  PLAN_HOW_MADE_LABEL,
  PLAN_FILE_GO_TO_STEP,
  PLAN_FILE_NEW,
  PLAN_FILE_TEST,
  PLAN_FILES_COL_CHANGE,
  PLAN_FILES_COL_FILE,
  PLAN_FILES_COL_STEPS,
  PLAN_STEP_COPY_LINK,
  PLAN_STEP_LINK_COPIED,
  PLAN_STEP_LINK_FAILED,
  PLAN_STEP_MORE_FILES,
  PLAN_STEPS_COLLAPSE_ALL,
  PLAN_STEPS_EXPAND_ALL,
} from "./workCopy.js";

/** Sections that are receipts rather than the decision. Folded by default. */
const FOLDED_SECTIONS = new Set(["grounding", "how this plan was made", "critic findings"]);

/**
 * How much of the plan a reader is handed open.
 *
 *   full    every decision section open, the receipts folded one by one
 *   gate    the plan being approved: the steps open as titles, the rest folded
 *   record  a plan already decided (approved, or behind a stopped build):
 *           everything folded under its heading, so it reads as a contents page
 *
 * In `gate` and `record` the Summary prints under the strip as the plan's
 * opening lines (clamped, with "more"), and the three receipts fold together
 * as one "How it was made".
 */
export type PlanReaderMode = "full" | "gate" | "record";

/** Whether a section starts open in a mode. Undefined: the section's own default. */
function initialOpenFor(mode: PlanReaderMode, sectionKey: string): boolean | undefined {
  if (mode === "record") return false;
  if (mode === "gate") return sectionKey === "steps";
  return undefined;
}

/** Lines of the Summary shown before "more". */
const SUMMARY_LINES = 3;
/** Lines of an open Test strategy shown before "more". */
const TEST_STRATEGY_LINES = 3;
/** Lines of an untitled step's detail shown before "more". */
const PLAIN_STEP_LINES = 2;

/**
 * Bullets and numbered items, the two list shapes the page writes, at the top
 * level only: a titled step's cases are indented under it and are not items.
 */
const LIST_ITEM = /^(?:[-*]|\d+\.)\s+\S/;

function countItems(section: MarkdownSection | undefined): number {
  if (!section) return 0;
  return section.lines.filter((line) => LIST_ITEM.test(line)).length;
}

/** A bullet or a numbered item, as a marker to strip off a line's front. */
const LIST_MARKER = /^(?:[-*]|\d+\.)\s+/;

function firstLine(section: MarkdownSection | undefined): string | null {
  if (!section) return null;
  const line = section.lines.find((entry) => entry.trim() !== "");
  return line ? line.trim().replace(LIST_MARKER, "") : null;
}

/** One fact on the glance strip, and the section it jumps to. */
export interface PlanGlanceFact {
  label: string;
  value: string;
  section: string;
}

/**
 * The strip's facts, read off the sections by title. Exported so the read is
 * testable without a DOM: a plan with no Risk level section has no risk fact,
 * and a count is a count of list items the page actually wrote.
 */
export function planGlanceFacts(
  sections: MarkdownSection[],
  questionCount: number,
): PlanGlanceFact[] {
  const byTitle = new Map(sections.map((section) => [section.title.toLowerCase(), section]));
  const facts: PlanGlanceFact[] = [];
  const approach = firstLine(byTitle.get("approach"));
  if (approach) facts.push({ label: "approach", value: approach, section: "Approach" });
  const risk = firstLine(byTitle.get("risk level"));
  if (risk) facts.push({ label: "risk", value: risk, section: "Risk level" });
  const steps = countItems(byTitle.get("steps"));
  if (steps > 0) facts.push({ label: "steps", value: String(steps), section: "Steps" });
  const files = countItems(byTitle.get("files to change"));
  if (files > 0)
    facts.push({
      label: files === 1 ? "file" : "files",
      value: String(files),
      section: "Files to change",
    });
  const criteria = countItems(byTitle.get("acceptance criteria"));
  if (criteria > 0)
    facts.push({
      label: criteria === 1 ? "criterion" : "criteria",
      value: String(criteria),
      section: "Acceptance criteria",
    });
  if (questionCount > 0)
    facts.push({
      label: questionCount === 1 ? "question" : "questions",
      value: String(questionCount),
      section: PLAN_QUESTIONS_SECTION,
    });
  return facts;
}

/** Sections whose one-line body is exactly the fact the strip reads off them. */
const STRIP_ONLY_SECTIONS = new Set(["approach", "risk level"]);

/**
 * A section the strip already says in full: Approach or Risk level holding one
 * line and nothing else. Printing "tdd" again under its own heading is the
 * same fact twice; a section that says why keeps its place in the document.
 */
function saidByStrip(section: MarkdownSection): boolean {
  return (
    STRIP_ONLY_SECTIONS.has(section.title.toLowerCase()) &&
    section.lines.filter((line) => line.trim() !== "").length === 1
  );
}

/** A risk a reviewer should look at twice wears the attention colour. */
const ATTENTION_RISKS = new Set(["medium", "high", "critical"]);

function sectionId(title: string): string {
  return (
    "plan-" +
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  );
}

function stepAnchor(n: number): string {
  return `plan-step-${n}`;
}

/** The teaser on a folded section: its first line, or how many items it holds. */
function teaserOf(section: MarkdownSection): string | null {
  const items = countItems(section);
  if (items > 0) return `${items} ${items === 1 ? "item" : "items"}`;
  const line = firstLine(section);
  return line ? line.replace(/^#+\s*/, "") : null;
}

/**
 * The reader's own controls must never reach the document around them: the
 * stage document starts editing on a click anywhere inside it, and a jump or
 * a fold is not an edit. A plan page is read-only today, so nothing is behind
 * this yet; the guard is here so the day a plan becomes editable does not
 * turn every fold into an accidental edit.
 */
function own(handler: () => void) {
  return (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handler();
  };
}

/** Matches the CSS transition on `.wk-fold`, so a closing fold unmounts after it. */
const FOLD_MS = 220;

/**
 * A fold that animates its height (grid rows 0fr to 1fr) and keeps what it
 * hides out of the document: the body mounts when it opens and unmounts once
 * the close has played. Folded text is not in the page, so find-in-page and a
 * screen reader meet the plan the way a person scanning it does.
 */
function Fold({ open, children }: { open: boolean; children: ReactNode }) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = setTimeout(() => setMounted(false), FOLD_MS);
    return () => clearTimeout(timer);
  }, [open]);
  return (
    <div className={"wk-fold" + (open ? " is-open" : "")}>
      <div className="wk-fold-inner">{open || mounted ? children : null}</div>
    </div>
  );
}

function Chevron() {
  return (
    <span className="wk-chev" aria-hidden="true">
      <svg viewBox="0 0 10 10">
        <path d="M3 1.5 7 5l-4 3.5z" />
      </svg>
    </span>
  );
}

function basename(path: string): string {
  return path.split("/").pop() || path;
}

/** One file on a step's head: its name, with the full path on hover. */
function FileChip({ path, test }: { path: string; test?: boolean }) {
  return (
    <span className="wk-planchip" title={path} data-testid="plan-step-file">
      {test && <span className="wk-planchip-k">{PLAN_FILE_TEST}</span>}
      {basename(path)}
    </span>
  );
}

/** How many file chips a step's head shows before it says "+N". */
const MAX_HEAD_CHIPS = 3;

function StepChips({ step }: { step: PlanStepView }) {
  const all = [
    ...step.files.map((path) => ({ path, test: false })),
    ...step.testFiles.map((path) => ({ path, test: true })),
  ];
  if (all.length === 0) return null;
  const shown = all.slice(0, MAX_HEAD_CHIPS);
  const hidden = all.slice(MAX_HEAD_CHIPS);
  return (
    <span className="wk-planchips">
      {shown.map((file) => (
        <FileChip key={`${file.test}:${file.path}`} path={file.path} test={file.test} />
      ))}
      {hidden.length > 0 && (
        <span className="wk-planchip is-more" title={hidden.map((file) => file.path).join("\n")}>
          {PLAN_STEP_MORE_FILES(hidden.length)}
        </span>
      )}
    </span>
  );
}

/** The link on a step: copies the page's address with the step's anchor. */
function StepLink({ n }: { n: number }) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  // One beat at a time: a second click restarts it, and unmounting ends it.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const flash = (next: "copied" | "failed") => {
    if (timer.current) clearTimeout(timer.current);
    setState(next);
    timer.current = setTimeout(() => setState("idle"), 1400);
  };
  const fail = (err: unknown) => {
    // No clipboard, or the browser refused (an insecure context, a denied
    // permission). The step keeps its anchor; the button says it failed.
    console.warn("[plan reader] could not copy the step link", err);
    flash("failed");
  };
  const copy = () => {
    try {
      const url = new URL(window.location.href);
      url.hash = stepAnchor(n);
      const clipboard = navigator.clipboard;
      if (!clipboard) {
        fail(new Error("navigator.clipboard is unavailable"));
        return;
      }
      clipboard.writeText(url.toString()).then(() => flash("copied"), fail);
    } catch (err) {
      fail(err);
    }
  };
  const label =
    state === "copied"
      ? PLAN_STEP_LINK_COPIED
      : state === "failed"
        ? PLAN_STEP_LINK_FAILED
        : PLAN_STEP_COPY_LINK(n);
  return (
    <button
      type="button"
      className={"wk-planstep-link" + (state === "idle" ? "" : ` is-${state}`)}
      aria-label={label}
      title={label}
      onClick={own(copy)}
      data-testid={`plan-step-link-${n}`}
    >
      <svg viewBox="0 0 16 16" aria-hidden="true">
        <path d="M6.5 9.5a3 3 0 0 0 4.2 0l2-2a3 3 0 0 0-4.2-4.2l-.7.7M9.5 6.5a3 3 0 0 0-4.2 0l-2 2a3 3 0 0 0 4.2 4.2l.7-.7" />
      </svg>
    </button>
  );
}

function stepBody(step: PlanStepView): string {
  const cases = step.cases.map((entry) => `- ${entry}`).join("\n");
  return [step.detail, cases].filter(Boolean).join("\n\n");
}

/** A step folds when it has a title to show and a body to hide under it. */
function isFoldable(step: PlanStepView): boolean {
  return Boolean(step.title) && stepBody(step) !== "";
}

function PlanStep({
  step,
  open,
  onToggle,
}: {
  step: PlanStepView;
  open: boolean;
  onToggle: () => void;
}) {
  const body = stepBody(step);
  const number = <span className="wk-planstep-no">{step.number}</span>;
  // A step written before titles existed is its own detail: one row, no fold.
  if (!step.title) {
    return (
      <li
        className="wk-planstep is-plain"
        id={stepAnchor(step.number)}
        data-testid={`plan-step-${step.number}`}
      >
        <div className="wk-planstep-head">
          {number}
          <ClampText lines={PLAIN_STEP_LINES} className="wk-planstep-plain-clamp">
            <Markdown className="wk-planstep-plain" text={step.detail} />
          </ClampText>
        </div>
        <StepLink n={step.number} />
      </li>
    );
  }
  const foldable = isFoldable(step);
  return (
    <li
      className={"wk-planstep" + (open ? " is-open" : "")}
      id={stepAnchor(step.number)}
      data-testid={`plan-step-${step.number}`}
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        className="wk-planstep-head"
        aria-expanded={foldable ? open : undefined}
        disabled={!foldable}
        onClick={own(onToggle)}
        data-testid={`plan-step-toggle-${step.number}`}
      >
        {foldable ? <Chevron /> : <span className="wk-chev" aria-hidden="true" />}
        {number}
        <span className="wk-planstep-title">
          <Markdown className="wk-planstep-title-md" text={step.title} />
        </span>
        <StepChips step={step} />
      </button>
      <StepLink n={step.number} />
      {foldable && (
        <Fold open={open}>
          <Markdown className="wk-planstep-body wk-plansec-body" text={body} />
        </Fold>
      )}
    </li>
  );
}

function foldableSteps(steps: PlanStepView[]): number[] {
  return steps.filter(isFoldable).map((step) => step.number);
}

function PlanSteps({
  steps,
  openSteps,
  setOpenSteps,
}: {
  steps: PlanStepView[];
  openSteps: ReadonlySet<number>;
  setOpenSteps: (next: Set<number>) => void;
}) {
  const toggle = (n: number) => {
    const next = new Set(openSteps);
    if (next.has(n)) next.delete(n);
    else next.add(n);
    setOpenSteps(next);
  };
  return (
    <ol className="wk-plansteps" data-testid="plan-steps">
      {steps.map((step) => (
        <PlanStep
          key={step.number}
          step={step}
          open={openSteps.has(step.number)}
          onToggle={() => toggle(step.number)}
        />
      ))}
    </ol>
  );
}

/** The steps' one control, on the Steps heading: open every titled step, or fold them. */
function ExpandAllSteps({
  steps,
  openSteps,
  setOpenSteps,
}: {
  steps: PlanStepView[];
  openSteps: ReadonlySet<number>;
  setOpenSteps: (next: Set<number>) => void;
}) {
  const foldable = foldableSteps(steps);
  if (foldable.length < 2) return null;
  const allOpen = foldable.every((n) => openSteps.has(n));
  return (
    <button
      type="button"
      className="wk-plansteps-all"
      onClick={own(() => setOpenSteps(allOpen ? new Set() : new Set(foldable)))}
      data-testid="plan-steps-all"
    >
      {allOpen ? PLAN_STEPS_COLLAPSE_ALL : PLAN_STEPS_EXPAND_ALL}
    </button>
  );
}

function PlanFilesTable({
  files,
  onGoToStep,
}: {
  files: PlanFileView[];
  onGoToStep: (n: number) => void;
}) {
  return (
    <div className="wk-planfiles-wrap">
      <table className="wk-planfiles" data-testid="plan-files">
        <thead>
          <tr>
            <th>{PLAN_FILES_COL_FILE}</th>
            <th>{PLAN_FILES_COL_CHANGE}</th>
            <th>{PLAN_FILES_COL_STEPS}</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => {
            const dir = file.path.slice(0, file.path.length - basename(file.path).length);
            return (
              <tr key={file.path}>
                <td className="wk-planfiles-path" title={file.path}>
                  {dir && <span className="wk-planfiles-dir">{dir}</span>}
                  {basename(file.path)}
                </td>
                <td>
                  <span className={"wk-planfiles-kind" + (file.isNew ? " is-new" : "")}>
                    {file.isNew ? PLAN_FILE_NEW : PLAN_FILE_EDIT}
                  </span>
                </td>
                <td className="wk-planfiles-steps">
                  {file.steps.map((n) => (
                    <button
                      key={n}
                      type="button"
                      className="wk-planfiles-step"
                      title={PLAN_FILE_GO_TO_STEP(n)}
                      onClick={own(() => onGoToStep(n))}
                    >
                      {n}
                    </button>
                  ))}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function PlanSection({
  section,
  action,
  initialOpen,
  children,
}: {
  section: MarkdownSection;
  /** A control that sits at the end of the heading row, outside the toggle. */
  action?: ReactNode;
  /** Whether it starts open. Absent: the decision opens, the receipts fold. */
  initialOpen?: boolean;
  children?: ReactNode;
}) {
  const folded = FOLDED_SECTIONS.has(section.title.toLowerCase());
  const [open, setOpen] = useState(initialOpen ?? !folded);
  const body = section.lines.join("\n").trim();
  if (!body) return null;
  const teaser = !open ? teaserOf(section) : null;
  const id = sectionId(section.title);
  return (
    <section
      className={
        "wk-plansec is-" + id + (folded ? " is-evidence" : "") + (open ? " is-open" : " is-folded")
      }
      id={id}
      data-testid={`plan-section-${id}`}
      data-open={open ? "true" : "false"}
    >
      <h2 className="wk-plansec-h">
        <button
          type="button"
          className="wk-plansec-head"
          aria-expanded={open}
          onClick={own(() => setOpen((value) => !value))}
          data-testid={`plan-section-toggle-${id}`}
        >
          <Chevron />
          <span className="wk-plansec-title">{section.title}</span>
          {teaser && <span className="wk-plansec-teaser">{teaser}</span>}
        </button>
        {open && action}
      </h2>
      <Fold open={open}>
        <div className="wk-plansec-pad">
          {children ?? <Markdown className="wk-plansec-body" text={body} />}
        </div>
      </Fold>
    </section>
  );
}

/**
 * The plan's receipts as one fold: what the planner read, how the plan was
 * made, and what the critic said. A reviewer comes back to them only when
 * something above looks wrong, so they share one heading instead of three.
 */
function HowItWasMade({ sections }: { sections: MarkdownSection[] }) {
  const merged: MarkdownSection = {
    title: PLAN_HOW_MADE_LABEL,
    lines: sections.flatMap((section) => [`### ${section.title}`, ...section.lines, ""]),
  };
  return (
    <PlanSection section={merged} initialOpen={false}>
      {sections.map((section) => (
        <div className="wk-plan-made" key={section.title}>
          <div className="wk-plan-made-h">{section.title}</div>
          <Markdown className="wk-plansec-body" text={section.lines.join("\n").trim()} />
        </div>
      ))}
    </PlanSection>
  );
}

/** A `#plan-step-N` address in the page's URL, if it names a step. */
function linkedStep(): number | null {
  try {
    const match = /^#plan-step-(\d+)$/.exec(window.location.hash);
    return match ? Number(match[1]) : null;
  } catch {
    return null;
  }
}

export function PlanReader({
  markdown,
  questionCount = 0,
  mode = "full",
  between,
}: {
  /** The plan page's body, with the questions section already lifted out. */
  markdown: string;
  /** How many questions the block below the plan is asking; 0 prints no fact. */
  questionCount?: number;
  /** How much starts open (see `PlanReaderMode`). */
  mode?: PlanReaderMode;
  /**
   * What the reader must act on before the plan itself, at a plan gate: the
   * questions, the staff engineer's read, the proposed changes. Printed after
   * the strip and the summary, before the steps, so the strip and summary are
   * said once and the decision sits above the detail it decides.
   */
  between?: ReactNode;
}) {
  const sections = useMemo(() => splitSections(markdown), [markdown]);
  const shaped = mode !== "full";
  const facts = useMemo(() => planGlanceFacts(sections, questionCount), [sections, questionCount]);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const preamble = sections[0]?.lines.join("\n").trim() ?? "";
  const titled = sections.filter((section) => section.title !== "");
  // Lowercased on both sides: facts name the canonical title, a heading may not.
  const stripOnly = new Set(
    titled.filter(saidByStrip).map((section) => section.title.toLowerCase()),
  );
  const visible = titled.filter((section) => !stripOnly.has(section.title.toLowerCase()));
  // Shaped modes lift the Summary up under the strip and fold the receipts
  // into one section; full mode prints every section where the page put it.
  const summary = shaped
    ? visible.find((section) => section.title.toLowerCase() === "summary")
    : undefined;
  const receipts = shaped
    ? visible.filter((section) => FOLDED_SECTIONS.has(section.title.toLowerCase()))
    : [];
  const shown = visible.filter((section) => section !== summary && !receipts.includes(section));
  const summaryText = summary?.lines.join("\n").trim() ?? "";

  const steps = useMemo(() => {
    const section = sections.find((entry) => entry.title.toLowerCase() === "steps");
    return section ? parsePlanSteps(section.lines) : null;
  }, [sections]);
  const files = useMemo(() => {
    const section = sections.find((entry) => entry.title.toLowerCase() === "files to change");
    return section ? parsePlanFiles(section.lines, steps ?? []) : null;
  }, [sections, steps]);

  // Titled steps start folded: the list of titles IS the overview. A step the
  // URL links to starts open, and the page scrolls to it once.
  const [openSteps, setOpenSteps] = useState<Set<number>>(() => {
    const linked = linkedStep();
    return linked === null ? new Set() : new Set([linked]);
  });
  // The plan page can arrive after the reader mounts, so the scroll waits for
  // the step to exist, and happens once: a later render never yanks the page.
  const scrolledToLink = useRef(false);
  useEffect(() => {
    if (scrolledToLink.current) return;
    const linked = linkedStep();
    if (linked === null) return;
    const target = rootRef.current?.querySelector(`#${stepAnchor(linked)}`);
    if (!target) return;
    scrolledToLink.current = true;
    setOpenSteps((prev) => (prev.has(linked) ? prev : new Set(prev).add(linked)));
    target.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [steps]);

  // A jump is a scroll, never a navigation: the reader is inside one document
  // and the page around it (the gate, the rail) must not move. The questions
  // block lives wherever the case file put it (above the plan at a gate, at
  // the plan's foot elsewhere), so the jump looks it up from the reading
  // column, and from the page when the reader is mounted on its own.
  const jumpTo = (title: string) => {
    const root = rootRef.current;
    if (!root) return;
    // The questions a person answers (`QuestionCards`, its answered lines
    // first), else the decisions Reeve states when nothing is asked.
    const scope = root.closest(".tri-read") ?? root.ownerDocument;
    const target =
      title === PLAN_QUESTIONS_SECTION
        ? (scope.querySelector('[data-testid="plan-questions"]') ??
          scope.querySelector('[data-testid="stated-decisions"]'))
        : root.querySelector(`#${sectionId(title)}`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goToStep = (n: number) => {
    setOpenSteps((prev) => new Set(prev).add(n));
    rootRef.current
      ?.querySelector(`#${stepAnchor(n)}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const bodyFor = (section: MarkdownSection): ReactNode | undefined => {
    const key = section.title.toLowerCase();
    if (key === "steps" && steps)
      return <PlanSteps steps={steps} openSteps={openSteps} setOpenSteps={setOpenSteps} />;
    if (key === "files to change" && files)
      return <PlanFilesTable files={files} onGoToStep={goToStep} />;
    return undefined;
  };

  return (
    <div className="wk-planreader" ref={rootRef} data-testid="plan-reader">
      {facts.length > 0 && (
        <div className="wk-planglance" data-testid="plan-glance" aria-label="At a glance">
          {facts.map((fact) => {
            const attention =
              fact.label === "risk" && ATTENTION_RISKS.has(fact.value.toLowerCase());
            const className = "wk-planglance-fact" + (attention ? " is-attention" : "");
            const inner = (
              <>
                <span className="wk-planglance-lab">{fact.label}</span>
                <span className="wk-planglance-val">{fact.value}</span>
              </>
            );
            return stripOnly.has(fact.section.toLowerCase()) ? (
              // Said here in full, so there is no section to jump to.
              <span
                key={fact.label}
                className={className + " is-static"}
                data-testid={`plan-glance-${fact.label}`}
              >
                {inner}
              </span>
            ) : (
              <button
                key={fact.label}
                type="button"
                className={className}
                data-testid={`plan-glance-${fact.label}`}
                title={`Go to ${fact.section}`}
                onClick={own(() => jumpTo(fact.section))}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
      {preamble && <Markdown className="wk-plansec-body" text={preamble} />}
      {summaryText && (
        <ClampText lines={SUMMARY_LINES} className="wk-plan-summary" testId="plan-summary">
          <Markdown className="wk-plansec-body" text={summaryText} />
        </ClampText>
      )}
      {between}
      {between != null && shown.length > 0 && (
        <div className="cf-sect wk-plan-body-lab">{PLAN_BODY_LABEL}</div>
      )}
      {shown.map((section) => {
        const key = section.title.toLowerCase();
        const initialOpen = initialOpenFor(mode, key);
        const body =
          bodyFor(section) ??
          (shaped && key === "test strategy" ? (
            <ClampText lines={TEST_STRATEGY_LINES}>
              <Markdown className="wk-plansec-body" text={section.lines.join("\n").trim()} />
            </ClampText>
          ) : undefined);
        return (
          <PlanSection
            key={section.title}
            section={section}
            {...(initialOpen !== undefined ? { initialOpen } : {})}
            action={
              key === "steps" && steps ? (
                <ExpandAllSteps steps={steps} openSteps={openSteps} setOpenSteps={setOpenSteps} />
              ) : undefined
            }
          >
            {body}
          </PlanSection>
        );
      })}
      {receipts.length > 0 && <HowItWasMade sections={receipts} />}
    </div>
  );
}
